// Sheet templates (design 02 12.2): a new sheet can start from one of these
// (새 시트 → 서식에서 고르기). A template is just cells as typed, styles and
// column widths; the engine computes the formulas like any others.
import type { XStyle } from './xlsx/model'

export interface TemplateCell {
  row: number
  col: number
  input: string
  style?: XStyle
}

export interface SheetTemplate {
  id: string
  name: string // the new sheet's name
  title: string // shown in the list
  description: string
  cells: TemplateCell[]
  colWidths: Record<string, number>
  frozenRows: number
}

const HEAD: XStyle = { b: true, bg: '#e7e6e6', al: 'c' }
const WON: XStyle = { nf: '#,##0' }
const WON_BOLD: XStyle = { nf: '#,##0', b: true }
const PCT: XStyle = { nf: '0.00%' }
const INPUT_BG = '#fff2cc'

function inputStyle(s: XStyle): XStyle {
  return { ...s, bg: INPUT_BG }
}

/**
 * 대출 비교: two loans side by side (원리금 균등 상환), what each costs a
 * month and in all, and the first months of loan A's schedule. The yellow
 * cells are for the person to change.
 */
function loanComparison(): SheetTemplate {
  const cells: TemplateCell[] = []
  const put = (row: number, col: number, input: string, style?: XStyle): void => {
    cells.push(style ? { row, col, input, style } : { row, col, input })
  }
  put(0, 0, '항목', HEAD)
  put(0, 1, '대출 A', HEAD)
  put(0, 2, '대출 B', HEAD)
  put(0, 3, '차이 (B - A)', HEAD)
  const rows: [string, string, string, XStyle, boolean][] = [
    // label, A, B, style, input
    ['원금', '100000000', '100000000', WON, true],
    ['연 이자율', '4.5%', '3.9%', PCT, true],
    ['기간(개월)', '360', '240', {}, true],
    ['월 상환액', '=-PMT(B3/12,B4,B2)', '=-PMT(C3/12,C4,C2)', WON_BOLD, false],
    ['총 상환액', '=B5*B4', '=C5*C4', WON, false],
    ['총 이자', '=B6-B2', '=C6-C2', WON, false],
    ['첫 달 이자', '=-IPMT(B3/12,1,B4,B2)', '=-IPMT(C3/12,1,C4,C2)', WON, false],
    ['첫 달 원금', '=-PPMT(B3/12,1,B4,B2)', '=-PPMT(C3/12,1,C4,C2)', WON, false],
  ]
  rows.forEach(([label, a, b, style, input], i) => {
    const r = i + 1
    put(r, 0, label, { b: true })
    put(r, 1, a, input ? inputStyle(style) : style)
    put(r, 2, b, input ? inputStyle(style) : style)
    if (!input) put(r, 3, `=C${r + 1}-B${r + 1}`, style)
  })
  put(10, 0, '노란 칸을 바꾸면 나머지가 다시 계산됩니다. 아래는 대출 A의 처음 12개월입니다.', { i: true })
  put(12, 0, '회차', HEAD)
  put(12, 1, '이자', HEAD)
  put(12, 2, '원금', HEAD)
  put(12, 3, '남은 원금', HEAD)
  put(13, 0, '=SEQUENCE(MIN(12,B4))')
  put(13, 1, '=-IPMT($B$3/12,A14#,$B$4,$B$2)', WON)
  put(13, 2, '=-PPMT($B$3/12,A14#,$B$4,$B$2)', WON)
  put(13, 3, '=$B$2-SCAN(0,C14#,LAMBDA(s,x,s+x))', WON)
  return {
    id: 'loan-comparison',
    name: '대출 비교',
    title: '대출 비교',
    description: '두 대출의 월 상환액, 총 이자, 상환 일정 (원리금 균등)',
    cells,
    colWidths: { '0': 140, '1': 130, '2': 130, '3': 130 },
    frozenRows: 1,
  }
}

export const TEMPLATES: SheetTemplate[] = [loanComparison()]

export function templateById(id: string): SheetTemplate | undefined {
  return TEMPLATES.find((t) => t.id === id)
}
